import React, { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { mockCheckout, formatPriceMonthlyCents } from "@/lib/billingApi";
import { planDisplayName } from "@/lib/planMarketing";

/**
 * Demo checkout UI — always succeeds via POST /billing/checkout/mock.
 */
export default function MockCheckoutPanel({
  accessToken,
  plan,
  catalogEntry,
  confirmationEmail,
  onSucceeded,
  disabled,
}) {
  const [cardName, setCardName] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [expiry, setExpiry] = useState("");
  const [cvc, setCvc] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const priceLabel = formatPriceMonthlyCents(
    catalogEntry?.price_monthly_cents ?? null
  );

  const handlePay = async () => {
    if (busy || disabled) return;
    setBusy(true);
    setError(null);
    try {
      const data = await mockCheckout(accessToken, plan);
      setResult(data);
      onSucceeded?.(data);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Checkout failed"
      );
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-4">
        <p className="text-sm font-medium text-foreground">
          Payment confirmed (demo)
        </p>
        <p className="text-xs text-muted-foreground">
          {result.confirmation_message ||
            `A fake confirmation was sent to ${confirmationEmail || "your email"}.`}
        </p>
        <p className="text-xs font-mono text-muted-foreground">
          Receipt: {result.receipt_id}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Alert>
        <AlertDescription>
          Demo checkout — no card is charged. Confirming always succeeds and
          records a mock receipt.
        </AlertDescription>
      </Alert>
      <p className="text-sm">
        <span className="font-medium">{planDisplayName(plan)}</span>
        {" — "}
        <span className="tabular-nums">{priceLabel}/mo</span>
      </p>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="space-y-2">
        <Label htmlFor="mock-card-name">Name on card</Label>
        <Input
          id="mock-card-name"
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
          placeholder="Jane Doe"
          disabled={busy || disabled}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="mock-card-number">Card number</Label>
        <Input
          id="mock-card-number"
          value={cardNumber}
          onChange={(e) => setCardNumber(e.target.value)}
          placeholder="4242 4242 4242 4242"
          disabled={busy || disabled}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-2">
          <Label htmlFor="mock-expiry">Expiry</Label>
          <Input
            id="mock-expiry"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            placeholder="12/30"
            disabled={busy || disabled}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="mock-cvc">CVC</Label>
          <Input
            id="mock-cvc"
            value={cvc}
            onChange={(e) => setCvc(e.target.value)}
            placeholder="123"
            disabled={busy || disabled}
          />
        </div>
      </div>
      <Button
        type="button"
        onClick={handlePay}
        disabled={busy || disabled || !plan}
      >
        {busy ? "Processing…" : `Confirm ${priceLabel}/mo (demo)`}
      </Button>
    </div>
  );
}
